/**
 * 科目分数段编辑器（subject-score-bands-and-scores design D9.1，SC-53/SC-54 前端）。
 *
 * 作为 Form.Item 受控子组件：value = null（默认按比例派生）| 边界点数组（自定义切档）。
 * 默认态：说明文案 + 服务端派生档位预览条（不自派生，档位来自 subjects 接口）；
 * 自定义态：边界点逐行编辑（输入不钳制不纠正——对齐 PRD「输入控件不自动纠正越界，
 * 提交时拦截」口径；失焦行内校验空值/非整数/越界/严格升序，行内红字）+
 * 本地切分预览条（相邻边界共享切点，纯 UI 切分）+ 添加/删除边界（上限 11）。
 * 关闭开关恢复默认派生（提交 null）。
 */
import { useState } from 'react';
import { Button, InputNumber, Popconfirm, Space, Switch, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { DerivedScoreBand } from '../../api/subjects.api';

const { Text } = Typography;

/** 自定义边界点数量上限（对应最多 12 档，与服务端冻结一致）。 */
const MAX_EDGES = 11;

/** 预览条色板（按档序渐变蓝，超出档数循环取色）。 */
const BAND_COLORS = [
  '#e6f4ff', '#bae0ff', '#91caff', '#69b1ff', '#4096ff', '#1677ff',
  '#0958d9', '#003eb3', '#002c8c', '#001d66', '#00123f', '#000c2d',
];

/** 边界点数组 → 预览档区间（相邻边界共享切点 [0,e1-1]/[e1,e2-1]/…/[ek,N]）。 */
function previewBandsOf(edges: number[], fullScore: number): Array<{ min: number; max: number }> {
  const bounds: Array<{ min: number; max: number }> = [];
  let prev = 0;
  for (const edge of edges) {
    bounds.push({ min: prev, max: edge - 1 });
    prev = edge;
  }
  bounds.push({ min: prev, max: fullScore });
  return bounds;
}

/** 单行边界点校验：空值/非整数/越界/不严格大于前一有效边界 → 错误文案。 */
function rowErrorOf(
  edges: Array<number | null>,
  index: number,
  fullScore: number,
): string | null {
  const edge = edges[index];
  if (edge === null || edge === undefined) return '边界点不能为空';
  if (!Number.isInteger(edge) || edge < 1 || edge > fullScore - 1) {
    return `边界点须为 1-${fullScore - 1} 的整数`;
  }
  const prev = [...edges.slice(0, index)].reverse().find((e) => e !== null && e !== undefined);
  if (prev !== undefined && edge <= prev) {
    return `须大于前一有效边界 ${prev}（严格升序）`;
  }
  return null;
}

export interface ScoreBandEditorProps {
  /** 当前生效满分（边界上限 1~满分-1 与预览切分基准）。 */
  fullScore: number;
  /** 服务端当前派生档位（默认态预览与自定义初始边界来源；新建无派生可传空数组）。 */
  derivedScoreBands: DerivedScoreBand[];
  /** Form.Item 注入的受控值：null = 默认派生，数组 = 自定义边界点。 */
  value?: Array<number | null> | null;
  /** Form.Item 注入的变更回调。 */
  onChange?: (value: Array<number | null> | null) => void;
}

export function ScoreBandEditor({
  fullScore,
  derivedScoreBands,
  value,
  onChange,
}: ScoreBandEditorProps) {
  const isCustom = Array.isArray(value);
  const edges = isCustom ? value : [];
  // 失焦后才显示行内错误（输入过程容忍中间态）；增删行重置避免索引错位
  const [touchedRows, setTouchedRows] = useState<Set<number>>(new Set());

  /** 从服务端派生档位反推默认边界（档 i 的 min 即切点，前端不自派生）。 */
  const defaultEdges = derivedScoreBands.slice(1).map((b) => b.min);

  const switchCustom = (checked: boolean) => {
    setTouchedRows(new Set());
    onChange?.(checked ? defaultEdges.map((e) => e as number | null) : null);
  };

  const setEdge = (index: number, next: number | null) => {
    onChange?.(edges.map((e, i) => (i === index ? next : e)));
  };

  const addEdge = () => {
    if (edges.length >= MAX_EDGES) return;
    setTouchedRows(new Set());
    onChange?.([...edges, null]);
  };

  const removeEdge = (index: number) => {
    setTouchedRows(new Set());
    onChange?.(edges.filter((_, i) => i !== index));
  };

  const bands = isCustom
    ? previewBandsOf(edges.map((e) => e ?? 0), fullScore)
    : derivedScoreBands.map((b) => ({ min: b.min, max: b.max }));
  const canAdd = edges.length < MAX_EDGES && fullScore - 1 >= 1;

  return (
    <div data-testid="score-band-editor">
      <Space align="center" style={{ marginBottom: 8 }}>
        <Switch
          size="small"
          checked={isCustom}
          onChange={switchCustom}
          data-testid="score-band-custom-switch"
        />
        <Text>自定义分数段</Text>
        {!isCustom && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            未开启时按默认比例派生（60%/70%/80%/90%）
          </Text>
        )}
      </Space>

      {/* 档位预览条：每档一段，宽度按分值跨度伸缩 */}
      {bands.length > 0 && (
        <div style={{ display: 'flex', gap: 2, marginBottom: 8 }} data-testid="score-band-preview">
          {bands.map((b, i) => (
            <div
              key={`${b.min}-${b.max}`}
              style={{
                flex: Math.max(b.max - b.min + 1, 1),
                background: BAND_COLORS[i % BAND_COLORS.length],
                color: i >= 4 ? '#fff' : '#1f1f1f',
                borderRadius: 4,
                padding: '2px 6px',
                fontSize: 12,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {b.min}-{b.max}
            </div>
          ))}
        </div>
      )}
      {!isCustom && derivedScoreBands.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          保存后按科目满分自动生成分数段
        </Text>
      )}

      {isCustom && (
        <div>
          {edges.map((edge, index) => {
            const error = rowErrorOf(edges, index, fullScore);
            const showError = error !== null && touchedRows.has(index);
            return (
              <div
                key={index}
                style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 4 }}
                data-testid={`score-band-edge-row-${index}`}
              >
                <div style={{ flex: 1 }}>
                  <InputNumber
                    size="small"
                    style={{ width: 120 }}
                    value={edge}
                    onChange={(v) => setEdge(index, v === null ? null : Number(v))}
                    onBlur={() => setTouchedRows((prev) => new Set(prev).add(index))}
                    status={showError ? 'error' : undefined}
                    placeholder={`1-${fullScore - 1}`}
                  />
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                    边界 {index + 1}
                  </Text>
                  {showError && (
                    <Text type="danger" style={{ fontSize: 12, display: 'block', marginLeft: 4 }}>
                      {error}
                    </Text>
                  )}
                </div>
                <Popconfirm
                  title="删除该边界？"
                  description="相邻两档将合并为一档"
                  okText="删除"
                  cancelText="取消"
                  onConfirm={() => removeEdge(index)}
                >
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            );
          })}
          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addEdge}
            disabled={!canAdd}
            data-testid="score-band-add-edge"
          >
            添加边界
          </Button>
          <Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginTop: 4 }}
          >
            边界点为升序正整数（1~{fullScore - 1}，最多 {MAX_EDGES} 个），相邻边界共享切点；共{' '}
            {edges.length + 1} 档
          </Text>
        </div>
      )}
    </div>
  );
}
